import type { RefObject } from 'react';
import { useCallback, useState } from 'react';

import { remToPx } from '@utils/dom-utils';

import {
  getWeightPerPixel,
  swallowNextClick,
  useColumnResize,
} from './use-column-resize';

export interface ResizeColumn {
  /**
   * Fixed rem width; excluded from resizing and from the flex distribution.
   */
  fixedWidth?: number;
  /**
   * The column id.
   */
  id: string;
  /**
   * Maximum resized width (flex weight).
   */
  maxSize?: number;
  /**
   * Minimum resized width (flex weight).
   */
  minSize?: number;
  /**
   * Whether the column may be resized (defaults to true).
   */
  resizable?: boolean;
  /**
   * The column's base width (flex weight).
   */
  size: number;
}

export interface UseTableResizeOptions {
  /**
   * The visible columns, in render order.
   */
  columns: ResizeColumn[];
  /**
   * Scrollport over the flex area, whose canvas width scales a drag delta into
   * weight space.
   */
  containerRef: RefObject<HTMLElement>;
  /**
   * Whether resizing is active.
   */
  enabled: boolean;
}

export interface UseTableResizeResult {
  /**
   * The current per-column widths (flex weights), keyed by column id.
   */
  columnWidths: Record<string, number>;
  /**
   * Returns a mousedown handler for a column's resize grip.
   */
  getResizeHandler: (columnId: string) => (event: React.MouseEvent) => void;
  /**
   * The id of the column being resized, or null when idle.
   */
  resizingColumnId: string | null;
}

const isFlexColumn = (column: ResizeColumn) =>
  column.fixedWidth == null && (column.resizable ?? true);

/**
 * Drives column resizing while preserving the legacy fixed-rem-vs-flex feel: a
 * drag grows the pulled column and redistributes the delta equally across the
 * resizable columns to its right, keeping the table filled. Reuses the shared
 * redistribution math from useColumnResize.
 */
export const useTableResize = ({
  columns,
  containerRef,
  enabled,
}: UseTableResizeOptions): UseTableResizeResult => {
  const { calcWidthIncrement, getWidthsForColumns, initialColumnWidths } =
    useColumnResize({
      columns: columns.map(column => ({
        id: column.id,
        maxWidth: column.maxSize,
        minWidth: column.minSize,
        width: column.size,
      })),
    });

  const [columnWidths, setColumnWidths] =
    useState<Record<string, number>>(initialColumnWidths);

  // Reseed only when the column set changes, so an in-progress resize (or a
  // data refresh that keeps the same columns) doesn't discard the user's widths.
  // Adjusting during render (rather than in an effect) avoids painting one frame
  // with the previous table's widths.
  const columnKey = columns.map(column => column.id).join('|');
  const [seededKey, setSeededKey] = useState(columnKey);
  if (seededKey !== columnKey) {
    setSeededKey(columnKey);
    setColumnWidths(initialColumnWidths);
  }

  const [resizingColumnId, setResizingColumnId] = useState<string | null>(null);

  const getResizeHandler = useCallback(
    (columnId: string) => (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidths = columnWidths;
      const resizedIndex = columns.findIndex(column => column.id === columnId);
      const resized = columns[resizedIndex];
      if (!resized) return;

      const neighborColumns = columns
        .filter((column, index) => index > resizedIndex && isFlexColumn(column))
        .map(column => ({
          id: column.id,
          maxWidth: column.maxSize,
          minWidth: column.minSize,
        }));

      const flexColumnsWidth = columns
        .filter(column => column.fixedWidth == null)
        .reduce(
          (sum, column) =>
            sum + (startWidths[column.id] ?? initialColumnWidths[column.id]),
          0,
        );
      const fixedColumnsWidth = columns
        .filter(column => column.fixedWidth != null)
        .reduce((sum, column) => sum + remToPx(column.fixedWidth!), 0);

      const startColumnWidth =
        startWidths[columnId] ?? initialColumnWidths[columnId];

      setResizingColumnId(columnId);

      const onMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;

        // A canvas too narrow to measure falls back to 1:1 inside the helper,
        // matching the legacy stack rather than abandoning the drag.
        const weightPerPixel = getWeightPerPixel({
          canvasWidth: containerRef.current?.scrollWidth ?? 0,
          fixedColumnsWidth,
          flexColumnsWidth,
        });

        const widthIncrement = calcWidthIncrement(
          { maxWidth: resized.maxSize, minWidth: resized.minSize },
          startColumnWidth,
          Math.floor(deltaX * weightPerPixel),
        );

        setColumnWidths({
          ...startWidths,
          ...getWidthsForColumns({
            columnToResize: { id: columnId },
            currentWidths: startWidths,
            neighborColumns,
            newColumnWidth: startColumnWidth + widthIncrement,
            widthDelta: widthIncrement,
          }),
        });
      };

      const onMouseUp = () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        setResizingColumnId(null);

        // Unconditional, so a press the user thinks better of and releases without
        // moving doesn't fall through to the header's sort handler either.
        swallowNextClick();
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [
      calcWidthIncrement,
      columns,
      columnWidths,
      containerRef,
      getWidthsForColumns,
      initialColumnWidths,
    ],
  );

  return {
    columnWidths: enabled ? columnWidths : {},
    getResizeHandler,
    resizingColumnId,
  };
};

export default useTableResize;
